import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowPathIcon, ExclamationTriangleIcon, InformationCircleIcon, ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { useTargetPerformance } from '../hooks/useDailyInstallations';

// Target v Performance — the NEO2 rebuild of the Power BI page.
//
// Incentive pay was removed from this page on 18/09/2026; it is a performance
// page now. The service still calculates it for the Operations overview.
// Originally built to be
// argued with: every figure can be traced, and the four things the calculation
// cannot do are stated on the page rather than left for someone to discover in
// a payroll query.
//
// Palette follows the report's theme, as the other rebuilt pages do.
const BRAND = '#711C91';

const num = (v) => Number(v || 0).toLocaleString('en-GB');
const dec = (v) => Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const pct = (v) => `${Math.round(Number(v || 0) * 100)}%`;
const thisMonth = () => new Date().toISOString().slice(0, 7);
const monthName = (m) => {
  const [y, mm] = String(m).split('-').map(Number);
  return new Date(Date.UTC(y, mm - 1, 1)).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};

// The four status bands the report defines, in the order it ranks them.
const TONES = {
  'Exceeding Target': { bg: '#DCFCE7', fg: '#166534', bar: '#16A34A' },
  'On Target': { bg: '#E9D8F0', fg: '#581678', bar: BRAND },
  'Needs Improvement': { bg: '#FEF3C7', fg: '#92400E', bar: '#D97706' },
  'Off Target': { bg: '#FEE2E2', fg: '#991B1B', bar: '#DC2626' },
  'No Target': { bg: '#F3F4F6', fg: '#4B5563', bar: '#9CA3AF' },
};

function Pill({ status }) {
  const t = TONES[status] || TONES['No Target'];
  return (
    <span
      className="inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold"
      style={{ background: t.bg, color: t.fg }}
    >
      {status}
    </span>
  );
}

// A target bar reads faster than the percentage beside it, and makes the
// difference between "just short" and "nowhere near" visible at a glance.
function Bar({ value, status }) {
  const t = TONES[status] || TONES['No Target'];
  const w = Math.max(0, Math.min(Number(value || 0), 1.5)) / 1.5;
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
      <div className="h-full rounded-full" style={{ width: `${w * 100}%`, background: t.bar }} />
    </div>
  );
}

function MultiSelect({ label, options, value, onChange, render }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  const toggle = (o) => onChange(value.includes(o) ? value.filter((v) => v !== o) : [...value, o]);
  const text = !value.length ? `All ${label.toLowerCase()}s`
    : value.length === 1 ? value[0] : `${label}s: ${value.length} selected`;
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex max-w-[15rem] items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm focus:outline-none ${
          value.length ? 'border-brand bg-brand-light text-brand' : 'border-gray-300 bg-white text-gray-700'}`}
      >
        <span className="truncate">{text}</span>
        <ChevronDownIcon className="h-3.5 w-3.5 shrink-0" />
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 max-h-80 w-64 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
          <div className="flex items-center justify-between border-b border-gray-100 px-3 py-1.5 text-xs">
            <button type="button" onClick={() => onChange([...options])} className="font-semibold text-brand hover:underline">Select all</button>
            <button type="button" onClick={() => onChange([])} className="font-semibold text-gray-500 hover:underline">Clear</button>
          </div>
          {options.map((o) => (
            <label key={o} className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
              <input type="checkbox" checked={value.includes(o)} onChange={() => toggle(o)} className="accent-brand" />
              <span className="flex-1 truncate">{render ? render(o) : o}</span>
            </label>
          ))}
          {!options.length && <p className="px-3 py-2 text-xs text-gray-400">Nothing to choose from.</p>}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub, accent }) {
  return (
    <div className={`rounded-lg border bg-white p-3 ${accent ? 'border-brand' : 'border-gray-200'}`}>
      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums text-gray-900">{value}</p>
      {sub ? <p className="text-xs text-gray-400">{sub}</p> : null}
    </div>
  );
}

function TargetPerformance() {
  // Date range (a single day = from == to). Defaults to this month so far.
  const [from, setFrom] = useState(() => {
    const d = new Date();
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)).toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(() => {
    const d = new Date();
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).toISOString().slice(0, 10);
  });
  // ISP filter removed 01/10/2026 (Ruan): Role replaces it, and Role, Status
  // and Region are all multi-select. Empty array = all.
  const [statuses, setStatuses] = useState([]);
  const [regions, setRegions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [q, setQ] = useState('');
  const [showWorking, setShowWorking] = useState(false);
  const month = String(from).slice(0, 7); // for the partial-cover message

  const filters = useMemo(() => ({ from, to }), [from, to]);
  const {
    data, isLoading, isFetching, refetch, isError,
  } = useTargetPerformance(filters);

  const all = data?.engineers || [];
  const t = data?.totals || {};
  const c = data?.caveats || {};
  const wd = data?.working_days || {};
  // Region choices come from the engineers themselves (their assigned region,
  // e.g. "Region B Gr. Manchester"), so the list always matches the month shown.
  const regionOpts = useMemo(
    () => [...new Set(all.map((r) => r.region).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [all],
  );
  // Filter the engineer LIST by region (not the jobs), so each engineer's
  // completed-vs-target stays whole and nobody looks under-target for working
  // more than one area.
  // Role is Match-Ups' role (sent as "department"), the job role
  // (Accredited Engineer, Supervisor, Hoist Engineer...).
  const NO_ROLE = 'No role set';
  const roleOf = (r) => r.department || NO_ROLE;
  const roleOpts = useMemo(
    () => [...new Set(all.map(roleOf))].sort((a, b) => (a === NO_ROLE) - (b === NO_ROLE) || a.localeCompare(b)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [all],
  );
  const needle = q.trim().toLowerCase();
  // Role and region decide WHO is in view, so the headline figures and the
  // status counts follow them; status then narrows the list within that.
  const scoped = all.filter((r) => (!roles.length || roles.includes(roleOf(r)))
    && (!regions.length || regions.includes(r.region))
    && (!needle || String(r.engineer || '').toLowerCase().includes(needle)));
  const rows = scoped.filter((r) => !statuses.length || statuses.includes(r.status));
  const narrowed = roles.length || regions.length || needle;
  const sum = (k) => scoped.reduce((a, r) => a + Number(r[k] || 0), 0);
  const count = (st) => scoped.filter((r) => r.status === st).length;
  const shownTotals = narrowed ? {
    completed: sum('completed'),
    adjusted: sum('adjusted'),
    monthly_target: sum('monthly_target'),
    over_target_jobs: sum('over_target_jobs'),
    exceeding: count('Exceeding Target'),
    on_target: count('On Target'),
    needs_improvement: count('Needs Improvement'),
    off_target: count('Off Target'),
  } : t;
  const toggleStatus = (st) => setStatuses((l) => (l.includes(st) ? l.filter((x) => x !== st) : [...l, st]));

  const sel = 'rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Target v Performance</h1>
          <p className="text-sm text-gray-500">
            Banded daily targets and progress against them.
          </p>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className={sel} title="From" />
          <span className="text-xs text-gray-400">to</span>
          <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className={sel} title="To" />
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search engineer…" className={`${sel} w-44`} />
          <MultiSelect label="Role" options={roleOpts} value={roles} onChange={setRoles} />
          <MultiSelect label="Status" options={Object.keys(TONES)} value={statuses} onChange={setStatuses} />
          <MultiSelect label="Region" options={regionOpts} value={regions} onChange={setRegions} />
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-md border border-gray-300 p-1.5 text-gray-500 hover:border-brand"
            title="Refresh"
          >
            <ArrowPathIcon className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {isError && <p className="mt-4 text-sm text-red-500">Could not load target performance.</p>}
      {isLoading && <p className="mt-4 text-sm text-gray-400">Loading…</p>}

      {data && (
        <>
          {/* A month the jobs mirror only partly covers cannot be read as a
              month. On a pay page that has to be impossible to miss. */}
          {c.partial_month ? (
            <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3">
              <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
              <p className="text-xs text-red-800">
                <strong>{monthName(month)} is not fully covered.</strong>{' '}
                The installations mirror starts on{' '}
                {new Date(c.data_from).toLocaleDateString('en-GB')}, every figure below counts only part of
                this month and understates every engineer.
              </p>
            </div>
          ) : null}

          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat
              label="Completed"
              value={num(shownTotals.completed)}
              sub={`${dec(shownTotals.adjusted)} adjusted`}
              accent
            />
            <Stat
              label="Monthly target"
              value={num(shownTotals.monthly_target)}
              sub={`${wd.total} working days`}
            />
            <Stat
              label="Working days"
              value={`${wd.so_far} / ${wd.total}`}
              sub={`${wd.remaining} remaining`}
            />
            <Stat
              label="Over target"
              value={dec(shownTotals.over_target_jobs)}
              sub="jobs, adjusted basis"
            />
          </div>

          {/* Status mix, clickable as a filter — the counts the report shows as
              four separate cards. */}
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              ['Exceeding Target', shownTotals.exceeding],
              ['On Target', shownTotals.on_target],
              ['Needs Improvement', shownTotals.needs_improvement],
              ['Off Target', shownTotals.off_target],
            ].map(([label, count]) => (
              <button
                key={label}
                type="button"
                onClick={() => toggleStatus(label)}
                className={`rounded-lg border bg-white p-3 text-left transition ${
                  statuses.includes(label) ? 'border-brand ring-1 ring-brand' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <Pill status={label} />
                  <span className="text-xl font-bold tabular-nums text-gray-900">{num(count)}</span>
                </div>
                <div className="mt-2"><Bar value={1} status={label} /></div>
              </button>
            ))}
          </div>

          {/* What the calculation could not do. Stated here rather than left to
              be discovered in a payroll query. */}
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <div className="text-xs text-amber-800">
              <p className="font-semibold">Four things this page cannot do, carried over from the report:</p>
              <ul className="mt-1 space-y-0.5">
                <li>
                  <strong>{num((c.no_target_engineers || []).length)} engineers have no target.</strong>{' '}
                  The band table only recognises exactly “Region B” or “Region N”. Dual-region
                  engineers use the daily target set in Match-Ups instead; anyone missing from
                  Match-Ups, or with no band and no target there, scores nothing.
                </li>
                <li>
                  <strong>The speed-support deduction matched {num(c.speed_deduction_matches)} jobs.</strong>{' '}
                  It looks for a support reason of “Speed/ General Support”, which neither mirrored
                  support list records, so in practice it never applies.
                </li>
                <li>
                  <strong>Bank holidays are not deducted.</strong>{' '}
                  The report used a holiday table generated inside the model that has no source we
                  hold, so targets in a month containing one are slightly high.
                </li>
              </ul>
              {(c.target_mismatches || []).length ? (
                <p className="mt-1">
                  <strong>{num(c.target_mismatches.length)} engineers</strong> have a stored target
                  in Match-Ups that disagrees with their band ({c.target_mismatches.slice(0, 4).map((m) => `${m.engineer} ${m.stored}≠${m.used}`).join(', ')}
                  ). The band table wins, as it does in the report.
                </p>
              ) : null}
            </div>
          </div>

          <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2">
              <h2 className="text-sm font-semibold text-gray-700">
                Engineers
                <span className="ml-2 font-normal text-gray-400">
                  {num(rows.length)} shown
                  {[...roles, ...statuses].length ? ` · ${[...roles, ...statuses].join(', ')}` : ''}
                </span>
              </h2>
              <button
                type="button"
                onClick={() => setShowWorking((v) => !v)}
                className="text-xs font-medium text-gray-500 hover:text-brand"
              >
                {showWorking ? 'Hide working' : 'Show working'}
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Engineer</th>
                    <th className="px-3 py-2">Band</th>
                    <th className="px-3 py-2 text-right">Daily</th>
                    <th className="px-3 py-2 text-right">Month</th>
                    <th className="px-3 py-2 text-right">Days</th>
                    {showWorking ? <th className="px-3 py-2 text-right">Sick</th> : null}
                    <th className="px-3 py-2 text-right">To date</th>
                    <th className="px-3 py-2 text-right">Adjusted</th>
                    {showWorking ? <th className="px-3 py-2 text-right">Weekday</th> : null}
                    {showWorking ? <th className="px-3 py-2 text-right">Weekend</th> : null}
                    <th className="px-3 py-2 text-right">Per day</th>
                    <th className="px-3 py-2">On track</th>
                    <th className="px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((r) => (
                    <tr key={r.email} className="hover:bg-gray-50">
                      <td className="px-3 py-1.5 font-medium text-gray-800">
                        {r.engineer}
                        {r.target_mismatch ? (
                          <span
                            className="ml-1 text-amber-600"
                            title={`Match-Ups target ${r.target_mismatch.stored}, band table ${r.target_mismatch.used} — band table used`}
                          >
                            ⚠
                          </span>
                        ) : null}
                        {r.department ? <span className="block text-[11px] font-normal text-gray-400">{r.department}</span> : null}
                      </td>
                      <td className="px-3 py-1.5 text-xs text-gray-500">
                        {r.banding || '—'}
                        <span className="ml-1 text-gray-300">{r.isp ? r.isp.replace('Region ', '') : ''}</span>
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">
                        {r.daily_target ? r.daily_target.toFixed(1) : '—'}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-gray-700">
                        {r.monthly_target || '—'}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">{num(r.install_days)}</td>
                      {showWorking ? (
                        <td className="px-3 py-1.5 text-right tabular-nums text-gray-400">
                          {r.sick_days || '—'}
                        </td>
                      ) : null}
                      <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">
                        {r.target_to_date || '—'}
                      </td>
                      <td className="px-3 py-1.5 text-right font-semibold tabular-nums" style={{ color: BRAND }}>
                        {dec(r.adjusted)}
                      </td>
                      {showWorking ? (
                        <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">{dec(r.weekday_adjusted)}</td>
                      ) : null}
                      {showWorking ? (
                        <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">{dec(r.weekend_installs)}</td>
                      ) : null}
                      <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">
                        {r.avg_per_day === null ? '—' : dec(r.avg_per_day)}
                      </td>
                      <td className="px-3 py-1.5">
                        <div className="flex items-center gap-2">
                          <span className="w-10 shrink-0 text-right text-xs tabular-nums text-gray-600">
                            {r.target_to_date ? pct(r.on_track) : '—'}
                          </span>
                          <span className="w-16 shrink-0"><Bar value={r.on_track} status={r.status} /></span>
                        </div>
                      </td>
                      <td className="px-3 py-1.5"><Pill status={r.status} /></td>
                    </tr>
                  ))}
                  {!rows.length ? (
                    <tr>
                      <td colSpan={showWorking ? 13 : 10} className="px-3 py-10 text-center text-gray-400">
                        No engineers match this filter.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            <p className="border-t border-gray-100 px-4 py-2 text-[11px] text-gray-400">
              Engineer details come from Match-Ups. Daily target is set by ISP and band (dual-region:
              the Match-Ups target). Monthly target is that rate across all{' '}
              {wd.total} working days; target to date is the rate across the engineer&apos;s
              installation days plus sick days. Adjusted = weighted completions − assistance given
              + assistance received + half a point per chargeable service call.
            </p>
          </div>

          {/* Named, because "14 engineers have no target" is a number somebody
              has to be able to act on. */}
          {(c.no_target_engineers || []).filter((e) => e.completed > 0).length ? (
            <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white">
              <h2 className="border-b border-gray-100 px-4 py-2 text-sm font-semibold text-gray-700">
                Completing work but carrying no target
              </h2>
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <tbody className="divide-y divide-gray-100">
                  {c.no_target_engineers.filter((e) => e.completed > 0).map((e) => (
                    <tr key={e.engineer}>
                      <td className="px-3 py-1.5 font-medium text-gray-800">{e.engineer}</td>
                      <td className="px-3 py-1.5 text-xs text-gray-500">{e.in_directory ? (e.isp || 'no ISP set') : 'not in Match-Ups'}</td>
                      <td className="px-3 py-1.5 text-xs text-gray-500">{e.banding || '—'}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-gray-700">
                        {num(e.completed)} completed
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="border-t border-gray-100 px-4 py-2 text-[11px] text-gray-400">
                Giving these engineers a target means adding them to Match-Ups with a band and a
                single-region ISP, or a daily target.
              </p>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export default TargetPerformance;
